import { getCsrfToken, getProviders, submitSignIn } from '#pplx/frontend/auth/core.js';
import { AuthError } from '#pplx/frontend/auth/errors.js';
import { sanitizedRedirectWindow } from '#pplx/frontend/common/sanitizedRedirectWindow.js';
/**
 * Client-side method to initiate a signin flow
 * or send the user to the signin page listing all possible providers.
 * Automatically adds the CSRF token to the request.
 *
 * [Documentation](https://next-auth.js.org/getting-started/client#signin)
 */
export async function signIn(provider, options = {}, authorizationParams) {
    const { callbackUrl = window.location.href, redirect = true, ...restOptions } = options ?? {};
    if (!provider) {
        sanitizedRedirectWindow(`/auth/signin?${new URLSearchParams({
            callbackUrl,
        })}`);
        return undefined;
    }
    try {
        const providers = await getProviders();
        if (!providers) {
            throw new AuthError('AUTH_PROVIDERS_ERROR', {
                cause: new Error('No providers found'),
            });
        }
        const providerData = providers[provider];
        if (!providerData) {
            throw new AuthError('AUTH_PROVIDER_NOT_FOUND_ERROR', {
                cause: new Error(`Provider ${provider} not found`),
            });
        }
        const isCredentials = providerData.type === 'credentials';
        const isEmail = providerData.type === 'email';
        const isSupportingReturn = isCredentials || isEmail;
        const signInUrl = isCredentials
            ? `/api/auth/callback/{provider}`
            : `/api/auth/signin/{provider}`;
        const { data, error, response } = await submitSignIn({
            signInUrl,
            provider,
            authorizationParams,
            body: {
                ...restOptions,
                csrfToken: await getCsrfToken(),
                callbackUrl,
                json: 'true',
            },
        });
        if (error) {
            const retryAfterHeader = response.headers.get('Retry-After');
            throw new AuthError('AUTH_SIGN_IN_ERROR', {
                cause: error,
                details: {
                    status: response.status,
                    retryAfter: retryAfterHeader ? Number(retryAfterHeader) : undefined,
                },
            });
        }
        if (redirect || !isSupportingReturn) {
            const url = data?.url ?? callbackUrl;
            sanitizedRedirectWindow(url);
            // If url contains a hash, the browser does not reload the page. We reload manually
            if (url.includes('#')) {
                window.location.reload();
            }
        }
        return data ?? undefined;
    }
    catch (err) {
        if (!(err instanceof AuthError)) {
            throw new AuthError('AUTH_SIGN_IN_ERROR', {
                cause: err,
            });
        }
        throw err;
    }
}
//# sourceMappingURL=signIn.js.map